import React, { useState, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Select from "react-select";
import {
    getRentalCategories,
    getRentalSubCategories,
    createRentalSubCategory,
    updateRentalSubCategory,
    deleteRentalSubCategory,
} from "../../../services/rentalService";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
    AdminPageLayout,
    AdminPageHeader,
    AdminPageCard,
    AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../../common/supplierPageMeta";
import { getTableEmptyProps } from "../../common/supplierTableEmpty";
import {
  SupplierAddButton,
  SupplierFilterToolbar,
  SupplierFilterField,
  SUPPLIER_FILTER_SELECT_CLASS,
} from "../../common/supplierPageUi";
import {
    AdminTable,
    AdminTableHead,
    AdminTableTh,
    AdminTableBody,
    AdminTableRow,
    AdminTableCell,
    AdminTableFooter,
    TableSearchInput,
    TableEmptyRow,
    EditActionButton,
    DeleteActionButton,
    TableActionsCell,
    StatusBadge,
    getStatusVariant,
} from "../../common/adminTable";
import { paginateClientList } from "../../../utils/clientPagination";
import { formatListingDateTime } from "../../../utils/formatListingDate";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

const statusOptions = [
    { value: 1, label: "Active" },
    { value: 0, label: "Inactive" },
];

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: "32px",
        height: "32px",
        backgroundColor: "#ffffff",
        borderColor: "#d1d5db",
        borderRadius: "0.5rem",
        boxShadow: "none",
        "&:hover": {
            borderColor: "#d1d5db",
            backgroundColor: "#ffffff",
        },
        "&:focus": {
            borderColor: "primary",
            boxShadow: "none",
            backgroundColor: "#ffffff",
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "0 8px",
        fontSize: "13px",
        height: "30px",
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: "0",
        fontSize: "13px",
    }),
    placeholder: (base) => ({
        ...base,
        fontSize: "14px",
    }),
    singleValue: (base) => ({
        ...base,
        fontSize: "14px",
        fontWeight: "500",
    }),
    indicatorsContainer: (base) => ({
        ...base,
        height: "30px",
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: "0 8px",
    }),
    clearIndicator: (base) => ({
        ...base,
        padding: "0 8px",
    }),
    menu: (base) => ({
        ...base,
        fontSize: "13px",
    }),
};

const RentalSubCategory = () => {
    const [filterCategoryId, setFilterCategoryId] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [parentCategoryId, setParentCategoryId] = useState("");
    const [name, setName] = useState("");
    const [status, setStatus] = useState(1);
    const [formError, setFormError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");

    const queryClient = useQueryClient();

    const { data: categoriesData } = useQuery({
        queryKey: ["rentalCategories"],
        queryFn: getRentalCategories,
    });
    const categories = categoriesData?.data || [];
    const categorySelectOptions = categories.map((c) => ({
        value: c.id,
        label: c.name,
    }));

    const {
        data: listRes,
        isLoading: listLoading,
        error: listError,
    } = useQuery({
        queryKey: ["rentalSubCategoriesList", filterCategoryId],
        queryFn: () => getRentalSubCategories(filterCategoryId || undefined),
    });

    const rows = useMemo(() => listRes?.data || [], [listRes]);

    const filteredRows = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((row) => {
            const name = String(row.name || "").toLowerCase();
            const categoryName = String(row.category?.name || "").toLowerCase();
            return name.includes(q) || categoryName.includes(q);
        });
    }, [rows, searchQuery]);

    const { data: pagedRows, pagination } = useMemo(
        () => paginateClientList(filteredRows, currentPage),
        [filteredRows, currentPage]
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [filteredRows, filterCategoryId, searchQuery]);

    const hasActiveFilters = !!filterCategoryId || !!searchQuery;

    const clearFilters = () => {
        setFilterCategoryId("");
        setSearchQuery("");
    };

    const createMutation = useMutation({
        mutationFn: createRentalSubCategory,
        onSuccess: () => {
            setFormError("");
            setModalOpen(false);
            setSuccessMessage("Sub category created successfully!");
            queryClient.invalidateQueries({ queryKey: ["rentalSubCategoriesList"] });
            queryClient.invalidateQueries({ queryKey: ["rentalSubCategories"] });
        },
        onError: (error) => {
            setErrorMessage(
                error.response?.data?.message || "Failed to create sub category"
            );
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => updateRentalSubCategory(id, data),
        onSuccess: () => {
            setFormError("");
            setEditModalOpen(false);
            setSuccessMessage("Sub category updated successfully!");
            queryClient.invalidateQueries({ queryKey: ["rentalSubCategoriesList"] });
            queryClient.invalidateQueries({ queryKey: ["rentalSubCategories"] });
        },
        onError: (error) => {
            setErrorMessage(
                error.response?.data?.message || "Failed to update sub category"
            );
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteRentalSubCategory,
        onSuccess: () => {
            setDeleteDialogOpen(false);
            setSelectedRow(null);
            setIsDeleting(false);
            setSuccessMessage("Sub category deleted successfully!");
            queryClient.invalidateQueries({ queryKey: ["rentalSubCategoriesList"] });
            queryClient.invalidateQueries({ queryKey: ["rentalSubCategories"] });
        },
        onError: (error) => {
            setIsDeleting(false);
            setErrorMessage(
                error.response?.data?.message || "Failed to delete sub category"
            );
        },
    });

    const openCreate = () => {
        setParentCategoryId(filterCategoryId || "");
        setName("");
        setStatus(1);
        setFormError("");
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setSelectedRow(row);
        setParentCategoryId(row.rental_category_id);
        setName(row.name);
        setStatus(row.is_active ? 1 : 0);
        setFormError("");
        setEditModalOpen(true);
    };

    const handleDelete = (row) => {
        setSelectedRow(row);
        setDeleteDialogOpen(true);
    };

    const saveCreate = () => {
        if (!parentCategoryId) {
            setFormError("Parent category is required");
            return;
        }
        if (!name.trim()) {
            setFormError("Name is required");
            return;
        }
        setFormError("");
        createMutation.mutate({
            rental_category_id: Number(parentCategoryId),
            name: name.trim(),
            is_active: status === 1,
        });
    };

    const saveEdit = () => {
        if (!name.trim()) {
            setFormError("Name is required");
            return;
        }
        setFormError("");
        updateMutation.mutate({
            id: selectedRow.id,
            data: {
                rental_category_id: Number(parentCategoryId),
                name: name.trim(),
                is_active: status === 1,
            },
        });
    };

    const confirmDelete = () => {
        setIsDeleting(true);
        deleteMutation.mutate(selectedRow.id);
    };

    return (
        <>
            <AdminPageLayout>
                <AdminPageHeader
                    title={SUPPLIER_PAGE_META.rentalSubCategory.title}
                    subtitle={SUPPLIER_PAGE_META.rentalSubCategory.subtitle}
                    actions={
                        <SupplierAddButton onClick={openCreate}>
                            Add New Sub Category
                        </SupplierAddButton>
                    }
                />

                <AdminPageCard>
                    <AdminPageCardToolbar>
                        <SupplierFilterToolbar
                            showReset={hasActiveFilters}
                            onReset={clearFilters}
                        >
                            <SupplierFilterField label="Search">
                                <TableSearchInput
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by sub category or category..."
                                    className="mt-1 w-full sm:w-full"
                                />
                            </SupplierFilterField>

                            <SupplierFilterField label="Category" className="md:col-span-4">
                                <select
                                    value={filterCategoryId}
                                    onChange={(e) => setFilterCategoryId(e.target.value)}
                                    className={SUPPLIER_FILTER_SELECT_CLASS}
                                >
                                    <option value="">All</option>
                                    {categories.map((category) => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </SupplierFilterField>
                        </SupplierFilterToolbar>
                    </AdminPageCardToolbar>

                    <AdminTable
                        className="shadow-none rounded-none"
                        footer={
                            !listLoading && !listError && filteredRows.length > 0 ? (
                                <AdminTableFooter>
                                    <Pagination
                                        pagination={pagination}
                                        onPageChange={setCurrentPage}
                                    />
                                </AdminTableFooter>
                            ) : null
                        }
                    >
                        <AdminTableHead>
                            <AdminTableTh>#</AdminTableTh>
                            <AdminTableTh>Category</AdminTableTh>
                            <AdminTableTh>Sub Category Name</AdminTableTh>
                            <AdminTableTh>Status</AdminTableTh>
                            <AdminTableTh>Created On</AdminTableTh>
                            <AdminTableTh align="right">Actions</AdminTableTh>
                        </AdminTableHead>
                        <AdminTableBody>
                            {listLoading ? (
                                <TableEmptyRow
                                    colSpan={6}
                                    variant="simple"
                                    emptyMessage="Loading sub categories..."
                                />
                            ) : listError ? (
                                <TableEmptyRow
                                    colSpan={6}
                                    variant="simple"
                                    emptyMessage={`Error loading sub categories: ${listError.message}`}
                                />
                            ) : pagedRows.length === 0 ? (
                                <TableEmptyRow
                                    colSpan={6}
                                    {...getTableEmptyProps("rentalSubCategory", {
                                        onAdd: openCreate,
                                        hasActiveFilters,
                                        onClearFilters: clearFilters,
                                    })}
                                />
                            ) : (
                                pagedRows.map((row, index) => (
                                    <AdminTableRow key={row.id}>
                                        <AdminTableCell>
                                            {(currentPage - 1) * 10 + index + 1}
                                        </AdminTableCell>
                                        <AdminTableCell>
                                            {row.category?.name || "—"}
                                        </AdminTableCell>
                                        <AdminTableCell>{row.name}</AdminTableCell>
                                        <AdminTableCell>
                                            <StatusBadge variant={getStatusVariant(row.is_active)}>
                                                {row.is_active ? "Active" : "Inactive"}
                                            </StatusBadge>
                                        </AdminTableCell>
                                        <AdminTableCell>
                                            {formatListingDateTime(row.created_at)}
                                        </AdminTableCell>
                                        <TableActionsCell>
                                            <EditActionButton
                                                showLabel={false}
                                                onClick={() => openEdit(row)}
                                            />
                                            <DeleteActionButton
                                                showLabel={false}
                                                onClick={() => handleDelete(row)}
                                            />
                                        </TableActionsCell>
                                    </AdminTableRow>
                                ))
                            )}
                        </AdminTableBody>
                    </AdminTable>
                </AdminPageCard>
            </AdminPageLayout>

            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                title="Add New Sub Category"
                size="sm"
                showCloseButton={false}
            >
                <div className="space-y-4 p-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Parent Category <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={categorySelectOptions}
                            value={
                                categorySelectOptions.find(
                                    (o) => String(o.value) === String(parentCategoryId)
                                ) || null
                            }
                            onChange={(o) => {
                                setParentCategoryId(
                                    o?.value != null ? String(o.value) : ""
                                );
                                setFormError("");
                            }}
                            placeholder="Select category"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sub Category Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                setFormError("");
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-primary ${
                                formError ? "border-red-500" : "border-gray-300"
                            }`}
                            placeholder="Enter sub category name"
                            onKeyPress={(e) => e.key === "Enter" && saveCreate()}
                            autoFocus
                        />
                        {formError && (
                            <p className="text-red-500 text-xs mt-1">{formError}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={statusOptions.find((option) => option.value === status)}
                            onChange={(selectedOption) =>
                                setStatus(selectedOption?.value)
                            }
                            options={statusOptions}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="Select status"
                            styles={selectStyles}
                        />
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => setModalOpen(false)}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={saveCreate}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                        >
                            Add Sub Category
                        </button>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                title="Edit Sub Category"
                size="sm"
                showCloseButton={false}
            >
                <div className="space-y-4 p-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Parent Category <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={categorySelectOptions}
                            value={
                                categorySelectOptions.find(
                                    (o) => String(o.value) === String(parentCategoryId)
                                ) || null
                            }
                            onChange={(o) =>
                                setParentCategoryId(
                                    o?.value != null ? String(o.value) : ""
                                )
                            }
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={selectStyles}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sub Category Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                setFormError("");
                            }}
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-primary ${
                                formError ? "border-red-500" : "border-gray-300"
                            }`}
                            placeholder="Enter sub category name"
                            onKeyPress={(e) => e.key === "Enter" && saveEdit()}
                            autoFocus
                        />
                        {formError && (
                            <p className="text-red-500 text-xs mt-1">{formError}</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status <span className="text-red-500">*</span>
                        </label>
                        <Select
                            value={statusOptions.find((option) => option.value === status)}
                            onChange={(selectedOption) =>
                                setStatus(selectedOption?.value)
                            }
                            options={statusOptions}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="Select status"
                            styles={selectStyles}
                        />
                    </div>

                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => setEditModalOpen(false)}
                            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={saveEdit}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                        >
                            Update Sub Category
                        </button>
                    </div>
                </div>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                title={
                    <div className="flex items-center gap-2">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                        Delete Sub Category
                    </div>
                }
                primaryButtonText="Delete"
                primaryButtonAction={confirmDelete}
                closeButtonText="Cancel"
                closeButtonAction={() => setDeleteDialogOpen(false)}
                isLoading={isDeleting}
            >
                <div className="mt-2">
                    <p className="text-sm text-gray-500">
                        Are you sure you want to delete this sub category? This action cannot
                        be undone.
                    </p>
                </div>
            </Dialog>

            {successMessage && (
                <Toster
                    message={successMessage}
                    type="success"
                    onClose={() => setSuccessMessage("")}
                />
            )}
            {errorMessage && (
                <Toster
                    message={errorMessage}
                    type="error"
                    onClose={() => setErrorMessage("")}
                />
            )}
        </>
    );
};

export default RentalSubCategory;
